import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  Modal,
  notification,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';
import { POSDeviceResponse } from '../../model/POS-device.model';
import POSDeviceService from '../../service/pos-device-service/POS-device.service';
import EditPOSDeviceModal from './EditPOSDeviceModal';

interface POSDeviceListTableProps {
  posDeviceList: POSDeviceResponse[];
  onSuccess: () => Promise<void>;
}

const POSDeviceListTable: React.FC<POSDeviceListTableProps> = ({
  posDeviceList,
  onSuccess,
}) => {
  const [posToDelete, setPosToDelete] = useState<POSDeviceResponse | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const screens = useBreakpoint();

  const handleDelete = async () => {
    if (posToDelete) {
      try {
        await POSDeviceService.deletePOSDevice(posToDelete.posId);
        onSuccess();
        notification.success({ message: 'Xóa thành công' });
        setPosToDelete(null);
      } catch (error: any) {
        const status = error?.response?.status;
        const message = error?.response?.data ?? error?.message;
        const is404 = status === 404;
        const is409 = status === 409; // Ràng buộc khóa ngoại: còn giao dịch liên quan
        if (is404) {
          await onSuccess();
          notification.warning({
            message: 'Thiết bị không tồn tại hoặc đã bị xóa',
            description: 'Danh sách đã được cập nhật.',
          });
        } else if (is409) {
          notification.warning({
            message: 'Không thể xóa thiết bị POS',
            description: message || 'Thiết bị đang được sử dụng bởi giao dịch. Vui lòng đổi thiết bị của các giao dịch trước khi xóa.',
          });
        } else {
          console.error('Error deleting POS device:', error);
          notification.error({
            message: 'Xóa thiết bị thất bại',
            description: message,
          });
        }
        setPosToDelete(null);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns: ColumnsType<POSDeviceResponse> = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 70,
      render: (_text, _record, index) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Mã thiết bị',
      dataIndex: 'deviceCode',
      key: 'deviceCode',
      render: (text, record) => (
        <div>
          {text}
          {!screens.sm && (
            <div className="text-xs text-gray-500 mt-1">{record.location}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Ngân hàng',
      dataIndex: 'bank',
      key: 'bank',
      render: (bank) => bank.name,
    },
    {
      title: 'Địa điểm',
      dataIndex: 'location',
      key: 'location',
      responsive: ['sm'],
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 'ACTIVE' ? (
          <Tag color="success">Hoạt động</Tag>
        ) : status === 'INACTIVE' ? (
          <Tag color="error">Không hoạt động</Tag>
        ) : (
          <Tag color="default">{status}</Tag>
        ),
    },
    {
      title: 'Hành động',
      key: 'action',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <EditPOSDeviceModal posDevice={record} onSuccess={onSuccess} />
          <Tooltip title="Xóa">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setPosToDelete(record)}
              className="flex items-center justify-center"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Table
        columns={columns}
        dataSource={posDeviceList}
        rowKey="posId"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: posDeviceList.length,
          onChange: handlePageChange,
          showSizeChanger: false,
          className: 'mt-4',
          position: ['bottomRight'],
        }}
        className="w-full border border-gray-100 shadow-md rounded-lg overflow-hidden"
        rowClassName="odd:bg-white even:bg-gray-50 hover:bg-gray-100"
        size="middle"
      />

      <Modal
        title="Xác nhận xoá"
        open={posToDelete !== null}
        onCancel={() => setPosToDelete(null)}
        footer={[
          <Button key="cancel" onClick={() => setPosToDelete(null)}>
            Hủy
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleDelete}>
            Xóa
          </Button>,
        ]}
      >
        <p>Bạn có chắc chắn muốn xóa thiết bị này?</p>
      </Modal>
    </div>
  );
};

export default POSDeviceListTable;
